import React, { useEffect, useState } from "react";
import { useLocation } from 'react-router-dom';
import "../FormTemplates.css";
import bg_template from "./I9FormTemplate.jpg";
import useAuth from '../../../../hooks/useAuth';
import dayjs from "dayjs";
import OverlayField from "../OverlayField";

/**
 * Form I-9 (edition 01/20/25), page 1: Section 1 and the signature block of Section 2.
 *
 * Every value is placed from the form's own field boxes: `[left, bottom, right, top]` in PDF
 * points on a 612 x 792 page, read straight from the fillable USCIS PDF (so a new edition is:
 * render the new page 1, read its field boxes, replace the numbers below). The documents that
 * go in the Section 2 List A/B/C rows come from the employer's verification and are not part
 * of this template.
 */
const PAGE_WIDTH = 611.976;
const PAGE_HEIGHT = 791.968;

const FIELDS = {
  lastName: [42.7, 604.8, 198.4, 620.0],
  firstName: [204.2, 604.8, 341.8, 620.0],
  middleInitial: [348.4, 604.8, 413.8, 620.0],
  otherLastNames: [420.4, 604.8, 576.1, 620.0],
  address: [41.9, 579.9, 228.1, 593.5],
  apartment: [233.6, 579.9, 299.3, 593.5],
  city: [305.9, 579.9, 455.3, 593.5],
  state: [461.2, 579.9, 502.4, 593.5],
  zip: [509.9, 579.9, 575.3, 593.5],
  dob: [41.8, 553.6, 140.9, 567.5],
  ssn: [150.0, 553.4, 255.0, 567.2],
  email: [264.2, 553.6, 450.1, 567.5],
  phone: [456.1, 553.5, 575.3, 567.6],
  citizen: [181.7, 523.3, 191.2, 532.8],
  nationalOfUs: [181.7, 511.3, 191.2, 520.8],
  permanentResident: [181.7, 499.3, 191.2, 508.8],
  authorizedToWork: [182.0, 487.3, 191.2, 496.4],
  permanentResidentNumber: [390.6, 498.2, 575.5, 509.4],
  authorizedUntil: [390.2, 484.1, 449.5, 497.0],
  aNumber: [180.6, 444.2, 263.4, 455.4],
  i94Number: [276.6, 444.2, 383.4, 455.4],
  passport: [396.6, 444.2, 575.4, 455.4],
  employeeSignature: [42.1, 420.8, 365.3, 433.7],
  employeeDate: [372.4, 420.8, 474.1, 433.7],
  firstDay: [462.6, 111.3, 547.7, 126.6],
  employerRepresentative: [36.6, 79.6, 287.9, 99.2],
  employerSignature: [294.3, 79.6, 485.3, 99.2],
  employerDate: [489.9, 79.6, 567.9, 99.2],
  organizationName: [36.7, 53.0, 238.9, 66.7],
  organizationAddress: [245.8, 53.0, 575.0, 66.7],
};

const DEFAULT_ORGANIZATION = "Freeman";
const DEFAULT_ORGANIZATION_ADDRESS = "International Plaza #2, 14221 Dallas Pkwy Suite 200, Dallas, TX 75254, USA";

// The template is drawn 794px wide when downloaded and 359px wide in the mobile preview.
const DOWNLOAD_WIDTH = 794;
const PREVIEW_WIDTH = 359;

/** A field's box, in pixels, at the download or preview width. */
const boxFor = (rect, isDownload) => {
  const scale = (isDownload ? DOWNLOAD_WIDTH : PREVIEW_WIDTH) / PAGE_WIDTH;
  const [left, bottom, right, top] = rect;
  return {
    left: left * scale,
    top: (PAGE_HEIGHT - top) * scale,
    width: (right - left) * scale,
    height: (top - bottom) * scale,
  };
};

/** mm/dd/yyyy, the way a US form wants a date. Accepts ISO dates and the old dd-mm-yyyy. */
const usDate = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  const old = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (old) return `${old[2]}/${old[1]}/${old[3]}`;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : raw;
};

const text = (value) => (value === undefined || value === null ? "" : String(value).trim());

export default function I9FormTemplate({ data, isDownload = false }) {
  const { userConfig } = useAuth();
  const location = useLocation();

  const isSupervisor = Boolean(location?.pathname?.includes('i9-submissions'));
  const representativeName = isSupervisor
    ? `${userConfig?.firstName || ''} ${userConfig?.lastName || ''}`.trim()
    : text(data?.authorizedRepresentativeName);
  const representativeTitle = isSupervisor
    ? `${userConfig?.role?.charAt(0).toUpperCase() || ''}${userConfig?.role?.slice(1) || ''}`
    : text(data?.authorizedRepresentativeTitle);

  const [bgLoaded, setBgLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = bg_template;
    img.onload = () => setBgLoaded(true);
  }, []);

  const baseFont = isDownload ? 12 : 6.5;
  const field = (name) => ({ d: boxFor(FIELDS[name], true), p: boxFor(FIELDS[name], false) });
  const printed = (name, value) => {
    const { d, p } = field(name);
    return (
      <OverlayField isDownload={isDownload} d={{ ...d, fontSize: baseFont }} p={{ ...p, fontSize: baseFont }}>
        {value}
      </OverlayField>
    );
  };
  const tick = (name) => {
    const box = boxFor(FIELDS[name], isDownload);
    return (
      <div
        className="overlay"
        style={{
          ...box,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: isDownload ? 13 : 6.5,
          lineHeight: 1,
        }}
      >
        ✓
      </div>
    );
  };

  /* ---- Section 1 ---- */
  const middleInitial = text(data?.middleInitial) || text(data?.middleName).charAt(0).toUpperCase();
  const apartment = text(data?.apartNumber ?? data?.aptNumber);

  const ssnDigits = text(data?.ssnNumber || data?.ssn || data?.ssnMasked).replace(/\D/g, "");
  const ssn = ssnDigits.length >= 4 ? `XXX-XX-${ssnDigits.slice(-4)}` : "";

  const status = data?.citizenshipStatus;
  const statusBox = {
    unitedState: "citizen",
    ead: "nationalOfUs",
    presidentResident: "permanentResident",
    temporaryVisa: "authorizedToWork",
  }[status];

  // Items 3 and 4 each ask for an identifying number; which box it goes in depends on the status
  // and, for item 4, on which kind of number the employee gave.
  const number = text(data?.citizenshipNumber);
  const documentType = data?.documentType;
  const aNumber = text(data?.alienRegistrationNumber || data?.uscisNumber);
  const lprNumber = number || aNumber;
  const authorizedNumbers = {
    aNumber: documentType === "aNumber" ? number : text(data?.alienRegistrationNumber || data?.uscisNumber),
    i94Number: documentType === "i94Number" ? number : text(data?.i94AdmissionNumber),
    passport: documentType === "passportNumber" ? number : text(data?.foreignPassportNumber),
  };
  const passportWithCountry = authorizedNumbers.passport
    ? `${authorizedNumbers.passport}${data?.countryOfIssuance ? ` (${data.countryOfIssuance})` : ""}`
    : "";

  /* ---- Signatures and Section 2 ---- */
  const employeeSignedOn = usDate(data?.signedAt) || dayjs().format("MM/DD/YYYY");
  // The employer's date belongs to the employer's signature -- not printed until they have signed.
  const employerHasSigned = Boolean(data?.supervisorEsign);
  const employerSignedOn = employerHasSigned ? usDate(data?.employerSignedAt) || dayjs().format("MM/DD/YYYY") : "";
  const representative = [representativeName, representativeTitle].filter(Boolean).join(", ");

  const imageBox = (name) => ({
    ...boxFor(FIELDS[name], isDownload),
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-start",
    overflow: "hidden",
  });

  return (
    <div className={isDownload ? "w4-wrapper-download" : "w4-wrapper-preview"}>
      {bgLoaded && (<>
        <img src={bg_template} alt="I-9 Form" className="w4-bg" />

        {printed("lastName", text(data?.lastName))}
        {printed("firstName", text(data?.firstName))}
        {printed("middleInitial", middleInitial)}
        {printed("otherLastNames", text(data?.otherLastNames))}
        {printed("address", text(data?.address))}
        {printed("apartment", apartment)}
        {printed("city", text(data?.city))}
        {printed("state", text(data?.state))}
        {printed("zip", text(data?.zipCode))}
        {printed("dob", usDate(data?.dob))}
        {printed("ssn", ssn)}
        {printed("email", text(data?.email))}
        {printed("phone", text(data?.mobile || data?.phone))}

        {statusBox && tick(statusBox)}
        {status === "presidentResident" && printed("permanentResidentNumber", lprNumber)}
        {status === "temporaryVisa" && (<>
          {printed("authorizedUntil", usDate(data?.expirationDate))}
          {printed("aNumber", authorizedNumbers.aNumber)}
          {printed("i94Number", authorizedNumbers.i94Number)}
          {printed("passport", passportWithCountry)}
        </>)}

        {data?.signatureBase64 && (
          <div className="overlay" style={imageBox("employeeSignature")}>
            <img
              src={`${data.signatureBase64}`}
              alt="signature"
              style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
            />
          </div>
        )}
        {printed("employeeDate", employeeSignedOn)}

        {printed("firstDay", usDate(data?.joiningDate))}
        {printed("employerRepresentative", representative)}
        {data?.supervisorEsign && (
          <div className="overlay" style={imageBox("employerSignature")}>
            <img
              src={`${data.supervisorEsign}`}
              alt="signature"
              style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
            />
          </div>
        )}
        {printed("employerDate", employerSignedOn)}
        {printed("organizationName", text(data?.employerName) || DEFAULT_ORGANIZATION)}
        {printed("organizationAddress", text(data?.employerAddress) || DEFAULT_ORGANIZATION_ADDRESS)}
      </>)}
    </div>
  );
}
